import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { subscriptionService } from '../services/subscriptionService';

const SubscriptionContext = createContext({});

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, userProfile } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasProAccess, setHasProAccess] = useState(false);

  // Load subscription data when user changes
  useEffect(() => {
    const loadSubscription = async () => {
      if (!user) {
        setSubscription(null);
        setHasProAccess(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await subscriptionService?.getSubscription();
        if (!error && data) {
          setSubscription(data);
          setHasProAccess(subscriptionService?.hasProAccess(data));
        }
      } catch (error) {
        console.error('Subscription load error:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSubscription();
  }, [user, userProfile]);

  // Refresh subscription data
  const refreshSubscription = async () => {
    if (!user) return;

    try {
      const { data, error } = await subscriptionService?.getSubscription();
      if (!error && data) {
        setSubscription(data);
        setHasProAccess(subscriptionService?.hasProAccess(data));
      }
    } catch (error) {
      console.error('Subscription refresh error:', error);
    }
  };

  // Check if user can use a feature
  const canUse = async (feature, currentCount = 0) => {
    return await subscriptionService?.canUse(feature, currentCount);
  };

  // Create checkout session and redirect
  const upgradeToProCheckout = async () => {
    try {
      const { data, error } = await subscriptionService?.createCheckoutSession();
      if (error) {
        // Check if it's a configuration error
        if (error?.message?.includes('STRIPE_PRICE_ID_PRO') || error?.message?.includes('configuration')) {
          alert('Stripe is not fully configured. Please contact support or check the setup documentation.');
          console.error('Stripe configuration error:', error);
          throw new Error('Stripe configuration incomplete');
        }
        throw error;
      }
      
      if (data?.checkoutUrl) {
        window.location.href = data?.checkoutUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      throw error;
    }
  };

  // Open customer portal
  const openCustomerPortal = async () => {
    try {
      const { data, error } = await subscriptionService?.createPortalSession();
      if (error) throw error;
      
      if (data?.portalUrl) {
        window.location.href = data?.portalUrl;
      }
    } catch (error) {
      console.error('Portal error:', error);
      throw error;
    }
  };

  const value = {
    subscription,
    loading,
    hasProAccess,
    plan: subscription?.plan || 'FREE',
    subscriptionStatus: subscription?.subscription_status || 'inactive',
    currentPeriodEnd: subscription?.current_period_end,
    refreshSubscription,
    canUse,
    upgradeToProCheckout,
    openCustomerPortal,
    isPro: hasProAccess,
    isFree: !hasProAccess
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};